import { useState } from "react";
import { useNavigate } from "react-router";
import { Sheet } from "../components/Sheet";
import { useCampaign } from "./context";
import { MaterialList } from "./material";
import { CampaignError, useAction } from "./parts";
import { BeginSceneDialog, NewPersonDialog, NoteDialog, useSituation } from "./play";

/**
 * The quick bar at the table, for the game master only: open the active scene, a note into the running session, a new person,
 * reveal scene material, the next game day. On phones it sits fixed above the navigation, on larger screens in the head.
 */
export function QuickBar() {
  const campaign = useCampaign();
  const navigate = useNavigate();
  const action = useAction();
  const [dialog, setDialog] = useState<"scene" | "note" | "person" | "reveal" | null>(null);
  const { play } = campaign.head;
  const scene = campaign.term("scene");
  const day = campaign.term("day");
  const person = campaign.kindLabel("person");
  return (
    <>
      <nav className="quickbar" aria-label="Am Tisch">
        <button
          type="button"
          className="quick"
          aria-label={play.sceneId ? `${scene} öffnen` : `${scene} beginnen`}
          onClick={() => (play.sceneId ? navigate(campaign.entryPath(play.sceneId)) : setDialog("scene"))}
        >
          <span aria-hidden="true">▶</span>
          {scene}
        </button>
        <button type="button" className="quick" onClick={() => setDialog("note")}>
          <span aria-hidden="true">✎</span>
          Notiz
        </button>
        <button type="button" className="quick" aria-label={`${person} anlegen`} onClick={() => setDialog("person")}>
          <span aria-hidden="true">＋</span>
          {person}
        </button>
        <button type="button" className="quick" disabled={!play.sceneId} aria-label="Material aufdecken" onClick={() => setDialog("reveal")}>
          <span aria-hidden="true">◉</span>
          Aufdecken
        </button>
        <button
          type="button"
          className="quick"
          aria-label={`${day} +1`}
          title={play.date.de}
          disabled={action.busy}
          onClick={() => void action.run(() => campaign.apply({ type: "campaign.day", payload: { action: "advance", days: 1 } }))}
        >
          <span aria-hidden="true">☀</span>
          {day} +1
        </button>
      </nav>
      {action.error && (
        <div className="quickbar-error" role="alert">
          <CampaignError error={action.error} onClose={action.clear} />
          <button type="button" className="link" onClick={action.clear}>
            Schließen
          </button>
        </div>
      )}
      {dialog === "scene" && <BeginSceneDialog onClose={() => setDialog(null)} />}
      {dialog === "note" && <NoteDialog onClose={() => setDialog(null)} />}
      {dialog === "person" && <NewPersonDialog onClose={() => setDialog(null)} />}
      {dialog === "reveal" && <RevealSheet onClose={() => setDialog(null)} />}
    </>
  );
}

function RevealSheet({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const situation = useSituation();
  const scene = situation.data?.scene;
  return (
    <Sheet title={`Material aufdecken (${campaign.term("scene")})`} onClose={onClose}>
      {situation.error && <CampaignError error={situation.error} reload />}
      {!situation.data ? (
        <p className="muted">Lade …</p>
      ) : !scene ? (
        <p className="muted">{campaign.term("scene")}: gerade nichts aktiv.</p>
      ) : (
        <>
          <p>
            <strong>{scene.title}</strong>
          </p>
          <MaterialList material={scene.material} sceneId={scene.id} />
        </>
      )}
    </Sheet>
  );
}
